import Plugin from '../core/Plugin';
import { removeFromParent, createElement, appendChild } from '../utils/Dom';
import { systemProbe } from '../utils/util';
import log from 'loglevel';

/**
 * 水印组件
 *
 * @export
 * @class Watermark
 * @extends {Plugin}
 */
export default class Watermark extends Plugin {
  constructor() {
    super();
    this.elImageWapper = null;
    this.elImage = null;
    this.elCavans = null;
    this.observer = null;
  }

  init(opts = {}) {
    super.init(opts);
    this._allConfig = opts;
    this._watermarkCfg = JSON.parse(opts[Watermark.type]); // JSON.parse(this._allConfig.watermark);
    let watermarkCfg = this._watermarkCfg;
    if (watermarkCfg.enable === false) {
      this._removeAllWatermarkEle();
      return;
    }
    this._removeAllWatermarkEle();
    if (watermarkCfg.mode === 'text') {
      // 文本水印
      this._createTextWatermark(watermarkCfg.textConfig);

      this.initEvents();
    } else {
      // 图片水印
      this._createWatermark(watermarkCfg);
    }
  }

  static get type() {
    return 'plugin_watermark';
  }

  _removeAllWatermarkEle() {
    if (this.elImageWapper) {
      removeFromParent(this.elImageWapper);
      this.elImage.src = '';
      this.elImage.onerror = null;
      this.elImage = null;
      this.elImageWapper = null;
    }
    if (this.elCavans) {
      removeFromParent(this.elCavans);
      this.elCavans = null;
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  destroy() {
    super.destroy();
    this._removeAllWatermarkEle();
    this.player = null;
  }

  initEvents() {
    if (this._watermarkCfg.enable && this._watermarkCfg.mode === 'text' && this._watermarkCfg?.textConfig?.content) {
      const video = this._player.getVideo();
      if (video && !this.observer) {
        // 确保 el 是有效的 DOM 节点, 创建ResizeObserver实例并指定回调函数
        this.observer = new ResizeObserver(entries => {
          // 使用 requestAnimationFrame 节流回调. 解决：ResizeObserver loop completed with undelivered notifications.
          requestAnimationFrame(() => {
            for (let entry of entries) {
              const cr = entry.contentRect;
              // console.log(`元素的尺寸: ${cr.width}px x ${cr.height}px`);
              if (cr.width > 0 && cr.height > 0) {
                // 重新绘制文本水印
                this._createTextWatermark(this._watermarkCfg?.textConfig);
              }
            }
          });
        });
        // 开始监听DOM元素
        this.observer.observe(video);
      }
    }
  }

  /**
   * 创建文本水印
   * @param {*} textCfg 文本水印的设置参数
   *   content: '', // 文本内容
       size: '16px', // 文字大小
       color: '#fff', // 文字颜色
       alpha: 100, // 透明度
       multiline: false, // 是否多行
       angle: 0 // 倾斜角度
   */
  _createTextWatermark(textCfg = null) {
    if (!textCfg || !textCfg.content) return;
    try {
      if (this.elCavans) {
        removeFromParent(this.elCavans);
        this.elCavans = null;
      }

      const video = this._player.getVideo();
      const rect = video.getBoundingClientRect();
      const cw = rect.width;
      const ch = rect.height;
      const canvas = document.createElement('canvas');
      // 设置​原生画布尺寸（未设置（默认300×150）），和样式宽高不是一个
      // 设置Canvas的实际尺寸（内存中的尺寸），解决retina屏不清晰问题
      const dpr = Math.min(window.devicePixelRatio || 1, 2.4); // 获取设备像素比，最大限制2.4倍屏
      canvas.width = cw * dpr;
      canvas.height = ch * dpr;
      canvas.style.position = 'absolute';
      canvas.style.left = 0;
      canvas.style.top = 0;
      canvas.style.width = `${cw}px`; // 设置宽高控制页面显示大小
      canvas.style.height = `${ch}px`;
      canvas.style.zIndex = 2;
      canvas.style.overflow = 'hidden';
      canvas.style.pointerEvents = 'none';
      canvas.style.userSelect = 'none';
      canvas.style.opacity = textCfg.opacity / 100;

      // 创建2d绘制api
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      // 获取默认字体
      const rootFontFamily = (
        document.documentElement.currentStyle
          ? document.documentElement.currentStyle
          : window.getComputedStyle(document.documentElement)
      ).fontFamily;
      // 设置水印文字大小、字体、颜色
      ctx.font = `${textCfg.size} ${rootFontFamily}`;
      ctx.fillStyle = `${textCfg.color}`;
      ctx.save();
      // 设置旋转中心，将旋转中心移动到中间（默认是画布左上角 (0,0)）
      ctx.translate(cw / 2, ch / 2);
      // 旋转-45度（转换为弧度）
      const radians = textCfg.degrees * (Math.PI / 180);
      ctx.rotate(radians);

      // 文本内容
      const text = textCfg.content;
      const metrics = ctx.measureText(text);
      const fontWidth = metrics.width; // 文字宽度
      // 文字高度， 兼容性处理，fontBoundingBoxAscent属性值可能没有， 最终兜底的文字高度设置为16
      const fontHeight = metrics.fontBoundingBoxAscent
        ? Math.ceil(metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent)
        : metrics.actualBoundingBoxAscent
        ? Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent)
        : 16;
      // 横向偏移量
      const woffset = fontWidth + Math.ceil(fontWidth / text.length) * 4;
      // 竖向偏移量
      const hoffset = 6 * fontHeight;
      const fixbu = 3 * fontHeight;
      // 水印容器用正方形，边长为原容器的斜边，要保证旋转后能完全覆盖
      const maxSide = Math.ceil(Math.sqrt(Math.pow(cw, 2) + Math.pow(ch, 2)));
      const halfMaxSide = maxSide / 2;
      if (textCfg.multiline) {
        // 多行
        let flag = 0;
        for (let x = -halfMaxSide; x <= halfMaxSide; x = x + woffset) {
          flag++;
          let bu = flag % 2 === 1 ? fixbu : 0;
          for (let y = -halfMaxSide + bu; y <= halfMaxSide; y = y + hoffset) {
            ctx.fillText(text, x, y);
          }
        }
      } else {
        // 单行
        for (let x = -halfMaxSide; x <= halfMaxSide; x = x + woffset) {
          ctx.fillText(text, x, 0);
        }
      }
      ctx.restore();
      this.elCavans = canvas;
      appendChild(this._allConfig['id'], this.elCavans);
    } catch (error) {
      log.error('创建文本水印失败,', error);
    }
  }

  /**
   * 根据配置生成水印
   *
   * @param {*} [cfg=null]
   * @memberof Watermark
   */
  _createWatermark(cfg = null) {
    const { url, align, position, size, alpha } = cfg;
    if (!url) return;
    const p = this._analysisPosition(align, position);

    this.elImageWapper = createElement('div');
    this.elImageWapper.style.position = 'absolute';
    this.elImageWapper.style.userSelect = 'none';
    if (systemProbe().browser !== 'ie') {
      this.elImageWapper.style.display = 'flex';
    }
    this.elImageWapper.style.flexDirection = 'column';
    this.elImageWapper.style.zIndex = 2;
    // IE9, Firefox, Chrome, Opera 和 Safari
    this.elImageWapper.style.opacity = alpha / 100;
    // 针对 IE8 以及更早的版本
    this.elImageWapper.style.filter = `alpha(opacity = ${alpha})`;
    this.elImageWapper.style.pointerEvents = 'none';
    Object.assign(this.elImageWapper.style, p);
    Object.assign(this.elImageWapper.style, {
      width: size[0],
      height: size[1]
    });
    appendChild(this._allConfig['id'], this.elImageWapper);

    this.elImage = createElement('img', {
      src: url
    });
    this.elImage.style.objectFit = 'contain';
    this.elImage.onerror = this._imgOnError.bind(this);
    this.elImage.onload = e => {
      if (this.elImage.width < this.elImage.height) {
        this.elImage.style.height = '100%';
      } else {
        this.elImage.style.width = '100%';
      }
      this.elImageWapper.appendChild(this.elImage);
    };
  }

  _analysisPosition(a, p) {
    let xPos = '10px';
    let yPos = '10px';
    if (!Array.isArray(p)) {
      log.warn(`could not resolve position paramter, ${p}`);
      return {
        left: xPos,
        top: yPos
      };
    } else {
      xPos = p[0];
      yPos = p[1];
    }

    let result = {};
    const alphabets = a.toLowerCase().split('');
    alphabets.forEach(ab => {
      switch (ab) {
        case 't':
          result.top = yPos;
          result.justifyContent = 'flex-start';
          break;
        case 'b':
          result.bottom = yPos;
          result.justifyContent = 'flex-end';
          break;
        case 'l':
          result.left = xPos;
          result.alignItems = 'flex-start';
          break;
        case 'r':
          result.right = xPos;
          result.alignItems = 'flex-end';
          break;
        default:
          result.left = xPos;
          result.top = yPos;
          break;
      }
    });
    return result;
  }

  _imgOnError(err) {
    let evt = {
      type: 'error',
      details: 'Watermark load failed.'
    };
    this.player.emit(evt.type, evt);
    log.error('Watermark load failed.' + err);
  }
}
